import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldCheck, Mail, Copy, Ban, RefreshCw, ExternalLink,
  Activity, Clock, Building2, User as UserIcon,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const ADMIN_TOKEN_KEY = "masci.admin.token";

/**
 * AdminSandboxRequests — operational view of every sandbox-key request.
 *
 * Shows every prospect who hit POST /api/demo/request-access from
 * forgedopshq.com (or directly), the status of their issued token,
 * email delivery, session activity, and admin actions:
 *   • Copy magic-link URL
 *   • Re-send email
 *   • Revoke token
 *
 * Restrained Bloomberg-terminal aesthetic — dark #0A0A0A, mono
 * eyebrows, orange accents, no SaaS chrome.
 */
export default function AdminSandboxRequests() {
  const [rows, setRows]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const tok = window.localStorage.getItem(ADMIN_TOKEN_KEY) || "";
      const r = await axios.get(`${API}/demo/admin/sandbox-requests`, {
        headers: { "X-Admin-Token": tok },
        timeout: 15000,
      });
      setRows(r?.data?.requests || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load requests.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const copyUrl = (url) => {
    if (!url) return toast.error("No token URL available.");
    try {
      navigator.clipboard.writeText(url);
      toast.success("Magic-link copied to clipboard.");
    } catch { toast.error("Copy failed."); }
  };

  const resend = async (request_id) => {
    try {
      const tok = window.localStorage.getItem(ADMIN_TOKEN_KEY) || "";
      const r = await axios.post(
        `${API}/demo/admin/sandbox-requests/${request_id}/resend`,
        {},
        { headers: { "X-Admin-Token": tok }, timeout: 15000 },
      );
      const sent = r?.data?.delivery?.sent;
      toast[sent ? "success" : "error"](
        sent ? "Sandbox-key email re-sent." : `Re-send failed: ${r?.data?.delivery?.error || "unknown"}`,
      );
      fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-send failed.");
    }
  };

  const revoke = async (request_id, company) => {
    if (!window.confirm(`Revoke sandbox access for "${company}"?`)) return;
    try {
      const tok = window.localStorage.getItem(ADMIN_TOKEN_KEY) || "";
      await axios.post(
        `${API}/demo/admin/sandbox-requests/${request_id}/revoke`,
        {},
        { headers: { "X-Admin-Token": tok }, timeout: 15000 },
      );
      toast.success("Sandbox token revoked.");
      fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed.");
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#0A0A0A", color: "#FFFFFF" }} data-testid="admin-sandbox-requests">
      {/* Caution-stripe banner */}
      <div
        aria-hidden
        style={{
          height: 4,
          background: "repeating-linear-gradient(45deg, #FF6A00 0 12px, #0A0A0A 12px 24px)",
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4 mb-7">
          <div>
            <div
              className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.32em] mb-2"
              style={{ color: "#FF6A00" }}
            >
              <ShieldCheck className="w-3 h-3" />
              ForgedOps · Operational Sandbox · Request Console
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ letterSpacing: "-0.01em" }}>
              Sandbox-key requests
            </h1>
            <p className="text-sm mt-1" style={{ color: "#9A9A9A" }}>
              Every prospect who requested a 3-day operational sandbox key — issued, activated, expired, or revoked.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchRows}
              className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] font-bold h-9 px-3 rounded transition-colors"
              style={{ background: "#101010", color: "#FFFFFF", border: "1px solid #2A2A2A" }}
              data-testid="admin-sandbox-refresh"
            >
              <RefreshCw className="w-3 h-3" style={{ color: "#FF6A00" }} />
              Refresh
            </button>
            <Link
              to="/admin"
              className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] font-bold h-9 px-3 rounded transition-colors"
              style={{ background: "transparent", color: "#888", border: "1px solid #1A1A1A" }}
              data-testid="admin-sandbox-back"
            >
              ← Admin Console
            </Link>
          </div>
        </div>

        {/* Summary strip */}
        <SummaryStrip rows={rows} />

        {/* Table */}
        <div
          className="mt-6"
          style={{ background: "#0B0B0B", border: "1px solid #1A1A1A" }}
        >
          {loading ? (
            <div className="px-6 py-12 text-center font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: "#666" }}>
              Loading operational requests…
            </div>
          ) : error ? (
            <div className="px-6 py-12 text-center" data-testid="admin-sandbox-error">
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] mb-2" style={{ color: "#D32F2F" }}>
                Failed to load requests
              </div>
              <div className="text-sm" style={{ color: "#888" }}>{String(error)}</div>
            </div>
          ) : rows.length === 0 ? (
            <div className="px-6 py-12 text-center font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: "#666" }} data-testid="admin-sandbox-empty">
              No sandbox requests yet · waiting for forgedopshq.com submissions
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-sandbox-table">
                <thead>
                  <tr style={{ borderBottom: "1px solid #1A1A1A" }}>
                    {[
                      "Prospect", "Company / Source", "Status", "Activity",
                      "Issued", "Expires", "Actions",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-3 py-2.5 text-left font-mono text-[9px] uppercase tracking-[0.28em] font-bold"
                        style={{ color: "#666" }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <RequestRow
                      key={r.request_id}
                      row={r}
                      onCopy={() => copyUrl(r.token_url)}
                      onResend={() => resend(r.request_id)}
                      onRevoke={() => revoke(r.request_id, r.company)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer note */}
        <div
          className="mt-6 px-4 py-3 font-mono text-[9px] uppercase tracking-[0.28em] text-center"
          style={{ background: "#080808", color: "#555", border: "1px solid #161616" }}
        >
          Operational Sandbox · Admin-only console · Token plaintext is shown for copy / re-send convenience and never exposed publicly
        </div>
      </div>
    </div>
  );
}

/* ───────────────────── Sub-components ───────────────────── */

function SummaryStrip({ rows }) {
  const counts = {
    total:     rows.length,
    issued:    rows.filter((r) => r.status === "issued").length,
    activated: rows.filter((r) => r.status === "activated").length,
    expired:   rows.filter((r) => r.status === "expired").length,
    revoked:   rows.filter((r) => r.status === "revoked").length,
    emails_failed: rows.filter((r) => !r.email_sent).length,
  };
  const items = [
    { label: "Total requests",      value: counts.total,     tone: "#FFFFFF" },
    { label: "Awaiting activation", value: counts.issued,    tone: "#FF6A00" },
    { label: "Activated",           value: counts.activated, tone: "#2E7D32" },
    { label: "Expired",             value: counts.expired,   tone: "#888888" },
    { label: "Revoked",             value: counts.revoked,   tone: "#D32F2F" },
    { label: "Email delivery failed", value: counts.emails_failed, tone: counts.emails_failed > 0 ? "#D32F2F" : "#444" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="admin-sandbox-summary">
      {items.map((it) => (
        <div
          key={it.label}
          className="px-3 py-3"
          style={{ background: "#0B0B0B", border: "1px solid #1A1A1A", borderLeft: `2px solid ${it.tone}` }}
        >
          <div
            className="font-mono text-[9px] uppercase tracking-[0.28em] mb-1"
            style={{ color: "#666" }}
          >
            {it.label}
          </div>
          <div className="text-2xl font-bold tabular-nums" style={{ color: it.tone }}>
            {it.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function RequestRow({ row, onCopy, onResend, onRevoke }) {
  const issued = fmtDate(row.created_at);
  const expires = fmtDate(row.expires_at);
  const activity =
    (row.pages_viewed?.length || 0) > 0 ||
    (row.forms_submitted?.length || 0) > 0 ||
    (row.ctas_clicked?.length || 0) > 0;
  return (
    <tr
      style={{ borderBottom: "1px solid #161616" }}
      className="hover:bg-[#0F0F0F] transition-colors"
      data-testid={`admin-sandbox-row-${row.request_id}`}
    >
      {/* Prospect */}
      <td className="px-3 py-3 align-top">
        <div className="flex items-start gap-2">
          <UserIcon className="w-3.5 h-3.5 mt-0.5" style={{ color: "#666" }} />
          <div>
            <div className="font-medium" style={{ color: "#FFFFFF" }}>{row.full_name || "—"}</div>
            <div className="text-xs mt-0.5" style={{ color: "#9A9A9A" }}>{row.email}</div>
            {row.role && <div className="text-xs mt-0.5" style={{ color: "#666" }}>{row.role}</div>}
          </div>
        </div>
      </td>

      {/* Company / Source */}
      <td className="px-3 py-3 align-top">
        <div className="flex items-start gap-2">
          <Building2 className="w-3.5 h-3.5 mt-0.5" style={{ color: "#666" }} />
          <div>
            <div className="font-medium" style={{ color: "#FFFFFF" }}>{row.company || "—"}</div>
            {row.fleet_size && <div className="text-xs mt-0.5" style={{ color: "#666" }}>Fleet: {row.fleet_size}</div>}
            <div
              className="font-mono text-[9px] uppercase tracking-[0.22em] mt-1"
              style={{ color: "#FF6A00" }}
            >
              ↳ {row.source || "—"}
            </div>
          </div>
        </div>
      </td>

      {/* Status */}
      <td className="px-3 py-3 align-top">
        <StatusPill status={row.status} email_sent={row.email_sent} />
        {row.email_error && (
          <div className="text-[10px] mt-1.5" style={{ color: "#D32F2F" }} title={row.email_error}>
            email: {String(row.email_error).slice(0, 60)}…
          </div>
        )}
      </td>

      {/* Activity */}
      <td className="px-3 py-3 align-top">
        {activity ? (
          <div className="space-y-1">
            {row.pages_viewed?.length > 0 && (
              <div className="text-xs" style={{ color: "#FFFFFF" }}>
                {row.pages_viewed.length} pages
              </div>
            )}
            {row.forms_submitted?.length > 0 && (
              <div className="text-xs" style={{ color: "#FF6A00" }}>
                {row.forms_submitted.length} form(s) submitted
              </div>
            )}
            {row.ctas_clicked?.length > 0 && (
              <div className="text-xs" style={{ color: "#2E7D32" }}>
                {row.ctas_clicked.length} CTA(s) clicked
              </div>
            )}
            {row.auto_tour_completed && (
              <div className="text-xs" style={{ color: "#888" }}>auto-tour ✓</div>
            )}
            <div className="font-mono text-[9px] mt-1" style={{ color: "#555" }}>
              <Clock className="inline w-2.5 h-2.5 mr-1" />
              {fmtDate(row.last_seen_at)}
            </div>
          </div>
        ) : (
          <div className="text-xs" style={{ color: "#555" }}>no activity yet</div>
        )}
        <div className="font-mono text-[9px] mt-1" style={{ color: "#666" }}>
          {row.sessions_used}/{row.max_sessions || 10} sessions
        </div>
      </td>

      {/* Issued */}
      <td className="px-3 py-3 align-top">
        <div className="text-xs" style={{ color: "#9A9A9A" }}>{issued}</div>
      </td>

      {/* Expires */}
      <td className="px-3 py-3 align-top">
        <div className="text-xs" style={{ color: "#9A9A9A" }}>{expires}</div>
      </td>

      {/* Actions */}
      <td className="px-3 py-3 align-top">
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            onClick={onCopy}
            title="Copy magic-link URL"
            className="inline-flex items-center justify-center w-7 h-7 rounded transition-colors"
            style={{ background: "#101010", color: "#FF6A00", border: "1px solid #2A2A2A" }}
            data-testid={`admin-sandbox-copy-${row.request_id}`}
          >
            <Copy className="w-3 h-3" />
          </button>
          <a
            href={row.token_url}
            target="_blank"
            rel="noreferrer noopener"
            title="Open token URL"
            className="inline-flex items-center justify-center w-7 h-7 rounded transition-colors"
            style={{ background: "#101010", color: "#FFFFFF", border: "1px solid #2A2A2A" }}
            data-testid={`admin-sandbox-open-${row.request_id}`}
          >
            <ExternalLink className="w-3 h-3" />
          </a>
          <button
            onClick={onResend}
            title="Re-send email"
            className="inline-flex items-center justify-center w-7 h-7 rounded transition-colors"
            style={{ background: "#101010", color: "#FF6A00", border: "1px solid #2A2A2A" }}
            data-testid={`admin-sandbox-resend-${row.request_id}`}
          >
            <Mail className="w-3 h-3" />
          </button>
          <button
            onClick={onRevoke}
            disabled={row.status === "revoked"}
            title="Revoke sandbox access"
            className="inline-flex items-center justify-center w-7 h-7 rounded transition-colors disabled:opacity-30"
            style={{ background: "#101010", color: "#D32F2F", border: "1px solid #2A1010" }}
            data-testid={`admin-sandbox-revoke-${row.request_id}`}
          >
            <Ban className="w-3 h-3" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function StatusPill({ status, email_sent }) {
  const map = {
    issued:    { label: "ISSUED",    color: "#FF6A00", border: "rgba(255,106,0,0.4)" },
    activated: { label: "ACTIVATED", color: "#2E7D32", border: "rgba(46,125,50,0.5)" },
    expired:   { label: "EXPIRED",   color: "#888",    border: "#2A2A2A" },
    revoked:   { label: "REVOKED",   color: "#D32F2F", border: "rgba(211,47,47,0.5)" },
  };
  const m = map[status] || map.issued;
  return (
    <div className="flex flex-col gap-1">
      <span
        className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.28em] font-bold px-2 py-0.5 rounded w-fit"
        style={{ background: "#0F0F0F", color: m.color, border: `1px solid ${m.border}` }}
      >
        <Activity className="w-2.5 h-2.5" />
        {m.label}
      </span>
      {!email_sent && status !== "revoked" && (
        <span
          className="inline-flex items-center gap-1 font-mono text-[8px] uppercase tracking-[0.22em] px-1.5 py-0.5 w-fit"
          style={{ background: "#1A0808", color: "#FF7070", border: "1px solid #3A1A1A" }}
        >
          email failed
        </span>
      )}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}
